import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  TextField,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Switch,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  useTheme,
  alpha,
  InputAdornment,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
  Devices as DevicesIcon,
  PhotoCamera as PhotoCameraIcon,
  ChevronRight as ChevronRightIcon,
  Shield as ShieldIcon,
  Key as KeyIcon,
  Smartphone as SmartphoneIcon,
  Computer as ComputerIcon,
  Logout as LogoutIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  History as HistoryIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { useAuthStore } from '../../stores/authStore';
import { stringToColor, getInitials } from '../../utils/helpers';
import { format, formatDistanceToNow } from 'date-fns';
import api from '../../utils/api';

type SectionType = 'profile' | 'security' | 'sessions' | 'notifications';

interface Session {
  id: string;
  sessionId: string;
  device: string;
  browser: string;
  os: string;
  location: string;
  ipAddress: string;
  loginAt: string;
  isCurrent: boolean;
}

interface ActivityLog {
  id: string;
  type: string;
  description: string;
  ipAddress: string;
  userAgent: string;
  status: string;
  createdAt: string;
}

interface ProfileData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  twoFactorEnabled: boolean;
  language: string;
  theme: string;
  enableDesktopNotifications: boolean;
  enableEmailNotifications: boolean;
  enableSoundNotifications: boolean;
  lastLoginAt: string;
  createdAt: string;
}

const ProfilePage: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { user, refreshUser } = useAuthStore();
  
  const [activeSection, setActiveSection] = useState<SectionType>('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    displayName: '',
    phoneNumber: '',
  });

  const [sessions, setSessions] = useState<Session[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [loadingActivity, setLoadingActivity] = useState(false);

  const [securitySettings, setSecuritySettings] = useState({
    twoFactorEnabled: false,
    loginAlerts: true,
  });

  const [notificationSettings, setNotificationSettings] = useState({
    enableDesktopNotifications: true,
    enableEmailNotifications: false,
    enableSoundNotifications: true,
  });

  const [passwordDialog, setPasswordDialog] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [changingPassword, setChangingPassword] = useState(false);

  const fetchProfile = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/users/profile');
      const data = response.data?.data || response.data;
      setProfileData(data);
      setFormData({
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        displayName: data.email?.split('@')[0] || '',
        phoneNumber: data.phoneNumber || '',
      });
      setSecuritySettings({
        twoFactorEnabled: data.twoFactorEnabled || false,
        loginAlerts: true,
      });
      setNotificationSettings({
        enableDesktopNotifications: data.enableDesktopNotifications ?? true,
        enableEmailNotifications: data.enableEmailNotifications ?? false,
        enableSoundNotifications: data.enableSoundNotifications ?? true,
      });
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchSessions = useCallback(async () => {
    try {
      setLoadingSessions(true);
      const response = await api.get('/users/sessions');
      const data = response.data?.data || response.data || [];
      setSessions(data);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
      setSessions([]);
    } finally {
      setLoadingSessions(false);
    }
  }, []);

  const fetchActivity = useCallback(async () => {
    try {
      setLoadingActivity(true);
      const response = await api.get('/users/activity?limit=20');
      const data = response.data?.data || response.data || [];
      setActivityLogs(data);
    } catch (error) {
      console.error('Failed to fetch activity:', error);
      setActivityLogs([]);
    } finally {
      setLoadingActivity(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    if (activeSection === 'sessions') {
      fetchSessions();
    } else if (activeSection === 'security') {
      fetchActivity();
    }
  }, [activeSection, fetchSessions, fetchActivity]);

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [field]: event.target.value }));
  };

  const handleSaveProfile = async () => {
    setIsSaving(true);
    try {
      await api.put('/users/profile', {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phoneNumber: formData.phoneNumber,
      });
      await fetchProfile();
      await refreshUser();
      setIsEditing(false);
      setSnackbar({ open: true, message: 'Profile updated successfully', severity: 'success' });
    } catch (error: any) {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || 'Failed to update profile', 
        severity: 'error' 
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setSnackbar({ open: true, message: 'Passwords do not match', severity: 'error' });
      return;
    }
    if (passwordData.newPassword.length < 8) {
      setSnackbar({ open: true, message: 'Password must be at least 8 characters', severity: 'error' });
      return;
    }
    setChangingPassword(true);
    try {
      await api.put('/users/password', {
        oldPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });
      setPasswordDialog(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setSnackbar({ open: true, message: 'Password changed successfully', severity: 'success' });
    } catch (error: any) {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || 'Failed to change password', 
        severity: 'error' 
      });
    } finally {
      setChangingPassword(false);
    }
  };

  const handleTerminateSession = async (sessionId: string) => {
    try {
      await api.delete(`/users/sessions/${sessionId}`);
      await fetchSessions();
      setSnackbar({ open: true, message: 'Session terminated', severity: 'success' });
    } catch (error: any) {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || 'Failed to terminate session', 
        severity: 'error' 
      });
    }
  };

  const handleTerminateAllSessions = async () => {
    try {
      await api.delete('/users/sessions');
      await fetchSessions();
      setSnackbar({ open: true, message: 'All other sessions terminated', severity: 'success' });
    } catch (error: any) {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || 'Failed to terminate sessions', 
        severity: 'error' 
      });
    }
  };

  const handleSaveNotifications = async () => {
    try {
      await api.put('/users/settings', notificationSettings);
      setSnackbar({ open: true, message: 'Notification settings saved', severity: 'success' });
    } catch (error: any) {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || 'Failed to save settings', 
        severity: 'error' 
      });
    }
  };

  const menuItems = [
    { id: 'profile', label: 'Profile', icon: PersonIcon },
    { id: 'security', label: 'Security', icon: SecurityIcon },
    { id: 'sessions', label: 'Active Sessions', icon: DevicesIcon },
    { id: 'notifications', label: 'Notifications', icon: NotificationsIcon },
  ];

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'login': return <PersonIcon color="success" />;
      case 'logout': return <LogoutIcon color="info" />;
      case 'failed_login': return <CloseIcon color="error" />;
      case 'password_change': return <KeyIcon color="warning" />;
      default: return <HistoryIcon color="action" />;
    }
  };

  const renderProfileSection = () => (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Typography variant="h5" fontWeight={600} color="text.primary">
          Profile Information
        </Typography>
        {!isEditing ? (
          <Button variant="contained" startIcon={<EditIcon />} onClick={() => setIsEditing(true)}
            sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}>
            Edit
          </Button>
        ) : (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" startIcon={<CloseIcon />}
              onClick={() => {
                setIsEditing(false);
                setFormData({
                  firstName: profileData?.firstName || '',
                  lastName: profileData?.lastName || '',
                  displayName: profileData?.email?.split('@')[0] || '',
                  phoneNumber: profileData?.phoneNumber || '',
                });
              }}
              sx={{ borderRadius: 2, textTransform: 'none' }}>
              Cancel
            </Button>
            <Button variant="contained"
              startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
              onClick={handleSaveProfile} disabled={isSaving}
              sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}>
              Save
            </Button>
          </Box>
        )}
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
          <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 4, p: 3,
            bgcolor: isDark ? alpha('#fff', 0.02) : alpha('#000', 0.02), borderRadius: 2 }}>
            <Box sx={{ position: 'relative' }}>
              <Avatar sx={{ width: 80, height: 80, bgcolor: stringToColor(profileData?.email || user?.email || 'User'),
                fontSize: '1.75rem', fontWeight: 600 }}>
                {getInitials(profileData?.firstName && profileData?.lastName 
                  ? `${profileData.firstName} ${profileData.lastName}` 
                  : profileData?.email || user?.email || 'U')}
              </Avatar>
              <IconButton size="small" sx={{ position: 'absolute', bottom: -4, right: -4,
                bgcolor: theme.palette.primary.main, color: '#fff', width: 28, height: 28,
                '&:hover': { bgcolor: theme.palette.primary.dark } }}>
                <PhotoCameraIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                {profileData?.firstName && profileData?.lastName 
                  ? `${profileData.firstName} ${profileData.lastName}` 
                  : user?.username || profileData?.email?.split('@')[0] || 'User'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {profileData?.email || user?.email}
              </Typography>
              <Chip label={user?.role || 'User'} size="small" color="primary"
                sx={{ mt: 1, textTransform: 'capitalize', fontWeight: 500 }} />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', gap: 3 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ mb: 1, display: 'block' }}>
                  FIRST NAME
                </Typography>
                <TextField fullWidth value={formData.firstName} onChange={handleInputChange('firstName')}
                  disabled={!isEditing} placeholder="Enter first name" variant="outlined" size="small"
                  InputProps={{ startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                    </InputAdornment>
                  )}}
                  sx={{ '& .MuiOutlinedInput-root': { bgcolor: isDark ? alpha('#fff', 0.02) : '#fff' } }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ mb: 1, display: 'block' }}>
                  LAST NAME
                </Typography>
                <TextField fullWidth value={formData.lastName} onChange={handleInputChange('lastName')}
                  disabled={!isEditing} placeholder="Enter last name" variant="outlined" size="small"
                  sx={{ '& .MuiOutlinedInput-root': { bgcolor: isDark ? alpha('#fff', 0.02) : '#fff' } }} />
              </Box>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ mb: 1, display: 'block' }}>
                EMAIL ADDRESS
              </Typography>
              <TextField fullWidth value={profileData?.email || user?.email || ''} disabled variant="outlined" size="small"
                InputProps={{
                  startAdornment: <InputAdornment position="start"><EmailIcon sx={{ color: 'text.secondary', fontSize: 20 }} /></InputAdornment>,
                  endAdornment: <Chip label="Verified" size="small" color="success" sx={{ height: 22 }} />
                }}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: isDark ? alpha('#fff', 0.02) : '#fff' } }} />
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ mb: 1, display: 'block' }}>
                PHONE NUMBER
              </Typography>
              <TextField fullWidth value={formData.phoneNumber} onChange={handleInputChange('phoneNumber')}
                disabled={!isEditing} placeholder="Enter phone number" variant="outlined" size="small"
                InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ color: 'text.secondary', fontSize: 20 }} /></InputAdornment> }}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: isDark ? alpha('#fff', 0.02) : '#fff' } }} />
            </Box>

            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', gap: 4, p: 2, bgcolor: isDark ? alpha('#fff', 0.02) : alpha('#000', 0.02),
              borderRadius: 2, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Domain</Typography>
                <Typography variant="body2" fontWeight={500}>{user?.domain || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Account Status</Typography>
                <Typography variant="body2" fontWeight={500}>
                  <Chip label={user?.isActive ? 'Active' : 'Inactive'} size="small" 
                    color={user?.isActive ? 'success' : 'default'} sx={{ height: 20 }} />
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Last Login</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {profileData?.lastLoginAt 
                    ? formatDistanceToNow(new Date(profileData.lastLoginAt), { addSuffix: true }) : '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Member Since</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {profileData?.createdAt || user?.createdAt 
                    ? format(new Date(profileData?.createdAt || user?.createdAt || ''), 'MMM d, yyyy') : '-'}
                </Typography>
              </Box>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );

  const renderSecuritySection = () => (
    <Box>
      <Typography variant="h5" fontWeight={600} color="text.primary" sx={{ mb: 1 }}>Security Settings</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Manage your account security and authentication preferences
      </Typography>

      <List disablePadding>
        <Paper variant="outlined" sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
          <ListItem sx={{ py: 2 }}>
            <ListItemIcon>
              <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.1),
                display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <KeyIcon color="primary" />
              </Box>
            </ListItemIcon>
            <ListItemText primary={<Typography fontWeight={500}>Password</Typography>}
              secondary="Change your account password" />
            <Button variant="outlined" size="small" sx={{ borderRadius: 2, textTransform: 'none' }}
              onClick={() => setPasswordDialog(true)}>Change Password</Button>
          </ListItem>
        </Paper>

        <Paper variant="outlined" sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
          <ListItem sx={{ py: 2 }}>
            <ListItemIcon>
              <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: alpha(theme.palette.success.main, 0.1),
                display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ShieldIcon color="success" />
              </Box>
            </ListItemIcon>
            <ListItemText primary={<Typography fontWeight={500}>Two-Factor Authentication</Typography>}
              secondary={securitySettings.twoFactorEnabled ? "2FA is enabled" : "Add extra security"} />
            <Chip label={securitySettings.twoFactorEnabled ? 'Enabled' : 'Disabled'} 
              color={securitySettings.twoFactorEnabled ? 'success' : 'default'} size="small" />
          </ListItem>
        </Paper>
      </List>

      <Typography variant="h6" fontWeight={600} sx={{ mt: 4, mb: 2 }}>Recent Activity</Typography>
      
      {loadingActivity ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {[1, 2, 3].map(i => <Skeleton key={i} variant="rectangular" height={60} sx={{ borderRadius: 2 }} />)}
        </Box>
      ) : activityLogs.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
          <HistoryIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography color="text.secondary">No recent activity</Typography>
        </Paper>
      ) : (
        <List disablePadding>
          {activityLogs.slice(0, 5).map((log) => (
            <Paper key={log.id} variant="outlined" sx={{ mb: 1, borderRadius: 2, overflow: 'hidden' }}>
              <ListItem sx={{ py: 1.5 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>{getActivityIcon(log.type)}</ListItemIcon>
                <ListItemText
                  primary={<Typography variant="body2" fontWeight={500}>{log.description}</Typography>}
                  secondary={<Typography variant="caption" color="text.secondary">
                    {log.ipAddress} • {formatDistanceToNow(new Date(log.createdAt), { addSuffix: true })}
                  </Typography>} />
                <Chip label={log.status} size="small" color={log.status === 'success' ? 'success' : 'error'}
                  sx={{ height: 20, fontSize: '0.7rem' }} />
              </ListItem>
            </Paper>
          ))}
        </List>
      )}
    </Box>
  );

  const renderSessionsSection = () => (
    <Box>
      <Typography variant="h5" fontWeight={600} color="text.primary" sx={{ mb: 1 }}>Active Sessions</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Devices where you're currently logged in
      </Typography>

      {loadingSessions ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {[1, 2].map(i => <Skeleton key={i} variant="rectangular" height={80} sx={{ borderRadius: 2 }} />)}
        </Box>
      ) : sessions.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <DevicesIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography color="text.secondary">No active sessions found</Typography>
        </Paper>
      ) : (
        <>
          {sessions.map((session, index) => (
            <Paper key={session.id} variant="outlined"
              sx={{ mb: 2, borderRadius: 2, overflow: 'hidden',
                borderColor: index === 0 ? theme.palette.primary.main : undefined }}>
              <ListItem sx={{ py: 2 }}>
                <ListItemIcon>
                  <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.1),
                    display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {session.device?.includes('Mobile') || session.device?.includes('iPhone') || session.device?.includes('Android') 
                      ? <SmartphoneIcon color="primary" /> : <ComputerIcon color="primary" />}
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography fontWeight={500}>{session.browser} on {session.os || session.device}</Typography>
                      {index === 0 && <Chip label="Current" size="small" color="primary" sx={{ height: 20 }} />}
                    </Box>
                  }
                  secondary={
                    <Typography variant="body2" color="text.secondary" component="span">
                      {session.location || 'Unknown'} • {session.ipAddress} • {
                        session.loginAt ? formatDistanceToNow(new Date(session.loginAt), { addSuffix: true }) : 'Active now'
                      }
                    </Typography>
                  } />
                {index !== 0 && (
                  <Button variant="outlined" color="error" size="small" startIcon={<LogoutIcon />}
                    onClick={() => handleTerminateSession(session.id)}
                    sx={{ borderRadius: 2, textTransform: 'none' }}>Sign Out</Button>
                )}
              </ListItem>
            </Paper>
          ))}
          {sessions.length > 1 && (
            <Button variant="outlined" color="error" fullWidth startIcon={<LogoutIcon />}
              onClick={handleTerminateAllSessions}
              sx={{ mt: 2, borderRadius: 2, textTransform: 'none', py: 1.5 }}>
              Sign Out All Other Sessions
            </Button>
          )}
        </>
      )}
    </Box>
  );

  const renderNotificationsSection = () => (
    <Box>
      <Typography variant="h5" fontWeight={600} color="text.primary" sx={{ mb: 1 }}>Notification Preferences</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>Manage how you receive notifications</Typography>

      <List disablePadding>
        <Paper variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
          <ListItem sx={{ py: 2 }}>
            <ListItemIcon><NotificationsIcon color="primary" /></ListItemIcon>
            <ListItemText primary={<Typography fontWeight={500}>Desktop Notifications</Typography>}
              secondary="Show notifications on your desktop" />
            <Switch checked={notificationSettings.enableDesktopNotifications}
              onChange={(e) => setNotificationSettings(prev => ({ ...prev, enableDesktopNotifications: e.target.checked }))} />
          </ListItem>
        </Paper>

        <Paper variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
          <ListItem sx={{ py: 2 }}>
            <ListItemIcon><EmailIcon color="primary" /></ListItemIcon>
            <ListItemText primary={<Typography fontWeight={500}>Email Notifications</Typography>}
              secondary="Receive notifications via email" />
            <Switch checked={notificationSettings.enableEmailNotifications}
              onChange={(e) => setNotificationSettings(prev => ({ ...prev, enableEmailNotifications: e.target.checked }))} />
          </ListItem>
        </Paper>

        <Paper variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
          <ListItem sx={{ py: 2 }}>
            <ListItemIcon><SmartphoneIcon color="primary" /></ListItemIcon>
            <ListItemText primary={<Typography fontWeight={500}>Sound Notifications</Typography>}
              secondary="Play a sound when new mail arrives" />
            <Switch checked={notificationSettings.enableSoundNotifications}
              onChange={(e) => setNotificationSettings(prev => ({ ...prev, enableSoundNotifications: e.target.checked }))} />
          </ListItem>
        </Paper>
      </List>

      <Button variant="contained" onClick={handleSaveNotifications}
        sx={{ mt: 2, borderRadius: 2, textTransform: 'none' }}>Save Notification Settings</Button>
    </Box>
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', bgcolor: isDark ? '#0f172a' : '#f8fafc' }}>
      <Paper elevation={0} sx={{ width: 260, borderRight: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`,
        bgcolor: isDark ? '#1e293b' : '#fff', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ p: 3, textAlign: 'center', borderBottom: `1px solid ${isDark ? '#334155' : '#e2e8f0'}` }}>
          <Avatar sx={{ width: 72, height: 72, bgcolor: stringToColor(profileData?.email || user?.email || 'User'),
            fontSize: '1.5rem', fontWeight: 600, mx: 'auto', mb: 2 }}>
            {getInitials(profileData?.firstName && profileData?.lastName 
              ? `${profileData.firstName} ${profileData.lastName}` : profileData?.email || user?.email || 'U')}
          </Avatar>
          <Typography variant="subtitle1" fontWeight={600}>
            {profileData?.firstName && profileData?.lastName 
              ? `${profileData.firstName} ${profileData.lastName}` 
              : user?.username || profileData?.email?.split('@')[0] || 'User'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
            {profileData?.email || user?.email}
          </Typography>
        </Box>

        <List sx={{ flex: 1, py: 1 }}>
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeSection === item.id;
            return (
              <ListItemButton key={item.id} selected={isActive}
                onClick={() => setActiveSection(item.id as SectionType)}
                sx={{ mx: 1, borderRadius: 2, mb: 0.5,
                  '&.Mui-selected': { bgcolor: alpha(theme.palette.primary.main, 0.1),
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.15) } } }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Icon sx={{ color: isActive ? theme.palette.primary.main : 'text.secondary', fontSize: 22 }} />
                </ListItemIcon>
                <ListItemText primary={item.label}
                  primaryTypographyProps={{ fontWeight: isActive ? 600 : 400,
                    color: isActive ? 'primary' : 'text.primary', fontSize: '0.9rem' }} />
                <ChevronRightIcon sx={{ fontSize: 18, color: 'text.secondary', opacity: isActive ? 1 : 0 }} />
              </ListItemButton>
            );
          })}
        </List>
      </Paper>

      <Box sx={{ flex: 1, overflow: 'auto', p: 4 }}>
        <Box sx={{ maxWidth: 800 }}>
          {activeSection === 'profile' && renderProfileSection()}
          {activeSection === 'security' && renderSecuritySection()}
          {activeSection === 'sessions' && renderSessionsSection()}
          {activeSection === 'notifications' && renderNotificationsSection()}
        </Box>
      </Box>

      <Dialog open={passwordDialog} onClose={() => setPasswordDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Current Password" type={showPasswords.current ? 'text' : 'password'} fullWidth
              value={passwordData.currentPassword}
              onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
              InputProps={{ endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}>
                    {showPasswords.current ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              )}} />
            <TextField label="New Password" type={showPasswords.new ? 'text' : 'password'} fullWidth
              value={passwordData.newPassword}
              onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
              InputProps={{ endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}>
                    {showPasswords.new ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              )}} />
            <TextField label="Confirm New Password" type={showPasswords.confirm ? 'text' : 'password'} fullWidth
              value={passwordData.confirmPassword}
              onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
              InputProps={{ endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}>
                    {showPasswords.confirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              )}} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleChangePassword}
            disabled={changingPassword || !passwordData.currentPassword || !passwordData.newPassword}>
            {changingPassword ? <CircularProgress size={20} /> : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProfilePage;
